import React, { useState } from 'react';
import { X, ChevronRight, LayoutDashboard, Package, BarChart3, Truck, Users, ShoppingCart, Globe, Wallet, FileText, Settings, HelpCircle, Rocket, Boxes, ClipboardList, Tags } from 'lucide-react';

// ─── Palette commune ────────────────────────────────────────────────────────
const C = {
  bg:     '#111827',
  card:   '#1f2937',
  border: '#374151',
  muted:  '#6b7280',
  gray:   '#9ca3af',
  text:   '#d1d5db',
  white:  '#f9fafb',
  yellow: '#facc15',
  ydark:  '#ca8a04',
  green:  '#22c55e',
  red:    '#ef4444',
  blue:   '#3b82f6',
  purple: '#a855f7',
  indigo: '#6366f1',
};

const Screen = ({ children, height = 'auto' }) => (
  <div style={{ background: C.bg, borderRadius: 10, border: `1px solid ${C.border}`, overflow: 'hidden', width: '100%', height, fontFamily: 'system-ui,sans-serif', fontSize: 11, color: C.text }}>
    {children}
  </div>
);
const Row = ({ children, style = {} }) => <div style={{ display: 'flex', alignItems: 'center', ...style }}>{children}</div>;
const Col = ({ children, style = {} }) => <div style={{ display: 'flex', flexDirection: 'column', ...style }}>{children}</div>;
const Btn = ({ label, color = C.yellow, textColor = '#000', style = {} }) => (
  <div style={{ background: color, borderRadius: 6, padding: '4px 10px', color: textColor, fontWeight: 700, fontSize: 10, display: 'inline-block', ...style }}>{label}</div>
);
const Badge = ({ label, color = C.green }) => (
  <div style={{ background: color + '22', border: `1px solid ${color}55`, color, borderRadius: 20, padding: '1px 7px', fontSize: 9, fontWeight: 600, display: 'inline-block' }}>{label}</div>
);
const Field = ({ label, value, style = {} }) => (
  <div style={{ marginBottom: 6, ...style }}>
    <div style={{ color: C.muted, fontSize: 9, marginBottom: 2 }}>{label}</div>
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 5, padding: '5px 8px', color: C.text, fontSize: 10 }}>{value}</div>
  </div>
);
const SidebarItem = ({ label, active }) => (
  <div style={{ padding: '6px 10px', borderRadius: 6, background: active ? C.ydark : 'transparent', color: active ? '#000' : C.gray, fontWeight: active ? 700 : 400, fontSize: 10, marginBottom: 2 }}>{label}</div>
);
const Divider = ({ style = {} }) => <div style={{ borderTop: `1px dashed ${C.border}`, margin: '6px 0', ...style }} />;
const StatCard = ({ label, value, color = C.yellow }) => (
  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', flex: 1 }}>
    <div style={{ color: C.muted, fontSize: 9 }}>{label}</div>
    <div style={{ color, fontWeight: 700, fontSize: 14, marginTop: 2 }}>{value}</div>
  </div>
);

// ─── Maquettes par section ──────────────────────────────────────────────────

function MockConnexion() {
  return (
    <Screen>
      <Col style={{ alignItems: 'center', padding: 20 }}>
        <div style={{ width: 44, height: 44, background: '#fff', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 22 }}>🧳</span>
        </div>
        <div style={{ color: C.yellow, fontWeight: 700, fontSize: 13 }}>Mon entreprise</div>
        <div style={{ color: C.ydark, fontSize: 10, marginBottom: 14 }}>Démo</div>
        <div style={{ width: '100%', maxWidth: 240 }}>
          <Field label="Adresse email" value="admin@soulvalise.com" />
          <Field label="Mot de passe" value="••••••••" />
          <div style={{ background: C.ydark, borderRadius: 7, padding: '7px', textAlign: 'center', color: '#000', fontWeight: 700, fontSize: 11, marginTop: 8, cursor: 'pointer' }}>
            🔓 Se connecter
          </div>
          <div style={{ textAlign: 'center', color: C.muted, fontSize: 9, marginTop: 8 }}>Rôles : Propriétaire · Employé · Vendeur</div>
        </div>
      </Col>
    </Screen>
  );
}

function MockDashboard() {
  return (
    <Screen>
      <Row style={{ background: C.card, padding: '7px 10px', borderBottom: `1px solid ${C.border}`, gap: 6 }}>
        <div style={{ color: C.yellow, fontWeight: 700, fontSize: 11 }}>📊 Tableau de Bord</div>
        <div style={{ marginLeft: 'auto' }}><Btn label="Clôturer la journée" color={C.card} textColor={C.yellow} style={{ border: `1px solid ${C.yellow}` }} /></div>
      </Row>
      <Col style={{ padding: 10, gap: 6 }}>
        <Row style={{ gap: 6 }}>
          <StatCard label="Ventes Magasin" value="320 000 GNF" color={C.yellow} />
          <StatCard label="Vendeurs" value="185 000 GNF" color={C.green} />
          <StatCard label="Livraisons" value="75 000 GNF" color={C.blue} />
        </Row>
        <Row style={{ gap: 6 }}>
          <StatCard label="Total Encaissé" value="580 000 GNF" color={C.yellow} />
          <StatCard label="Articles Vendus" value="24 pcs" color={C.purple} />
          <StatCard label="⚠️ Ruptures Stock" value="3 articles" color={C.red} />
        </Row>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8 }}>
          <div style={{ color: C.gray, fontSize: 9, marginBottom: 5 }}>ACTIVITÉ RÉCENTE</div>
          {[
            { label: 'Vente magasin — 3 articles', val: '45 000 GNF', color: C.green },
            { label: 'Retour vendeur — Amadou D.', val: '−12 000 GNF', color: C.red },
            { label: 'Commande livrée N°42', val: '78 000 GNF', color: C.blue },
          ].map((r, i) => (
            <Row key={i} style={{ justifyContent: 'space-between', padding: '3px 0', borderBottom: i < 2 ? `1px solid ${C.border}` : 'none' }}>
              <span style={{ color: C.text, fontSize: 10 }}>{r.label}</span>
              <span style={{ color: r.color, fontWeight: 700, fontSize: 10 }}>{r.val}</span>
            </Row>
          ))}
        </div>
      </Col>
    </Screen>
  );
}

function MockProduits() {
  return (
    <Screen>
      <Row style={{ background: C.card, padding: '7px 10px', borderBottom: `1px solid ${C.border}`, gap: 6 }}>
        <div style={{ color: C.yellow, fontWeight: 700, fontSize: 11 }}>📦 Produits</div>
        <div style={{ marginLeft: 'auto' }}><Btn label="+ Nouveau produit" /></div>
      </Row>
      <Row style={{ padding: '6px 10px', gap: 6, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 5, padding: '4px 8px', color: C.muted, fontSize: 10 }}>🔍 Rechercher un produit…</div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 5, padding: '4px 8px', color: C.gray, fontSize: 10 }}>Catégorie ▾</div>
      </Row>
      <Col style={{ padding: 8, gap: 6 }}>
        {[
          { nom: 'Valise Cabine Rigide', cat: '🧳 Valises', variantes: 4, stock: 18, photo: '🟦' },
          { nom: 'Sac de Voyage Sport', cat: '👜 Sacs', variantes: 3, stock: 7, photo: '🟧' },
          { nom: 'Trolley Business 24\'\'', cat: '🧳 Valises', variantes: 2, stock: 0, photo: '🟥' },
        ].map((p, i) => (
          <Row key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, gap: 8 }}>
            <div style={{ width: 36, height: 36, background: C.border, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{p.photo}</div>
            <Col style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 11, color: C.white }}>{p.nom}</div>
              <div style={{ color: C.muted, fontSize: 9 }}>{p.cat} · {p.variantes} variantes</div>
            </Col>
            <Col style={{ alignItems: 'flex-end', gap: 3 }}>
              <Badge label={p.stock === 0 ? '❌ Rupture' : `Stock: ${p.stock}`} color={p.stock === 0 ? C.red : C.green} />
              <Row style={{ gap: 4 }}>
                <Btn label="✏️" color={C.card} textColor={C.yellow} style={{ border: `1px solid ${C.border}`, padding: '2px 6px' }} />
                <Btn label="📷" color={C.card} textColor={C.blue} style={{ border: `1px solid ${C.border}`, padding: '2px 6px' }} />
              </Row>
            </Col>
          </Row>
        ))}
      </Col>
    </Screen>
  );
}

function MockStock() {
  return (
    <Screen>
      <Row style={{ background: C.card, padding: '7px 10px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ color: C.yellow, fontWeight: 700, fontSize: 11 }}>📊 Gestion du Stock</div>
        <div style={{ marginLeft: 'auto' }}><Btn label="📋 Mouvements" color={C.card} textColor={C.gray} style={{ border: `1px solid ${C.border}` }} /></div>
      </Row>
      <div style={{ overflowX: 'auto', padding: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
          <thead>
            <tr style={{ background: C.card }}>
              {['Produit / Variante', 'Disponible', 'Réservé', 'Seuil', 'Statut', 'Action'].map(h => (
                <th key={h} style={{ padding: '5px 8px', textAlign: 'left', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              { nom: 'Valise Cabine / Noir', dispo: 18, res: 2, seuil: 5, ok: true },
              { nom: 'Sac Sport / Bleu M', dispo: 3, res: 0, seuil: 5, ok: false },
              { nom: 'Trolley 24\'\' / Gris', dispo: 0, res: 0, seuil: 3, ok: false },
            ].map((r, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : C.card + '44', borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: '5px 8px', color: C.white, fontWeight: 600 }}>{r.nom}</td>
                <td style={{ padding: '5px 8px', color: r.dispo === 0 ? C.red : C.green, fontWeight: 700 }}>{r.dispo}</td>
                <td style={{ padding: '5px 8px', color: C.gray }}>{r.res}</td>
                <td style={{ padding: '5px 8px', color: C.muted }}>{r.seuil}</td>
                <td style={{ padding: '5px 8px' }}><Badge label={r.ok ? '✅ OK' : r.dispo === 0 ? '❌ Rupture' : '⚠️ Faible'} color={r.ok ? C.green : C.red} /></td>
                <td style={{ padding: '5px 8px' }}><Btn label="Ajuster" color={C.indigo} textColor="#fff" style={{ fontSize: 9 }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ margin: '0 8px 8px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8 }}>
        <div style={{ color: C.muted, fontSize: 9, marginBottom: 4 }}>AJUSTEMENT MANUEL</div>
        <Row style={{ gap: 6 }}>
          <div style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 5, padding: '4px 8px', color: C.muted, fontSize: 10 }}>Type ▾</div>
          <div style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 5, padding: '4px 8px', color: C.muted, fontSize: 10 }}>Quantité</div>
          <Btn label="Valider" />
        </Row>
      </div>
    </Screen>
  );
}

function MockFournisseurs() {
  return (
    <Screen>
      <Row style={{ background: C.card, padding: '7px 10px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ color: C.yellow, fontWeight: 700, fontSize: 11 }}>🚚 Fournisseurs</div>
        <div style={{ marginLeft: 'auto' }}><Btn label="+ Fournisseur" /></div>
      </Row>
      <Col style={{ padding: 8, gap: 6 }}>
        {[
          { nom: 'Diallo Import', tel: '+224 620 000 001', spec: '🧳 Valises', nb: 5 },
          { nom: 'Konaté Bags & Co', tel: '+224 620 000 002', spec: '👜 Sacs', nb: 3 },
        ].map((f, i) => (
          <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
            <Row style={{ justifyContent: 'space-between', marginBottom: 5 }}>
              <div style={{ fontWeight: 700, color: C.white }}>{f.nom}</div>
              <Badge label={f.spec} color={C.yellow} />
            </Row>
            <div style={{ color: C.muted, fontSize: 9, marginBottom: 8 }}>📞 {f.tel} · {f.nb} approvisionnements</div>
            <Row style={{ gap: 5 }}>
              <Btn label="📦 Nouvel approvisionnement" color={C.green} textColor="#fff" style={{ fontSize: 9 }} />
              <Btn label="📋 Historique" color={C.card} textColor={C.gray} style={{ border: `1px solid ${C.border}`, fontSize: 9 }} />
            </Row>
          </div>
        ))}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8 }}>
          <div style={{ color: C.muted, fontSize: 9, marginBottom: 5 }}>COMPARAISON PRIX — Valise Cabine Rigide</div>
          <Row style={{ justifyContent: 'space-between' }}>
            <span style={{ color: C.text, fontSize: 10 }}>Diallo Import</span>
            <span style={{ color: C.green, fontWeight: 700, fontSize: 10 }}>42 000 GNF ✅ Moins cher</span>
          </Row>
          <Row style={{ justifyContent: 'space-between' }}>
            <span style={{ color: C.text, fontSize: 10 }}>Konaté Bags</span>
            <span style={{ color: C.muted, fontSize: 10 }}>48 000 GNF</span>
          </Row>
        </div>
      </Col>
    </Screen>
  );
}

function MockVendeurs() {
  return (
    <Screen>
      <Row style={{ background: C.card, padding: '7px 10px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ color: C.yellow, fontWeight: 700, fontSize: 11 }}>👥 Vendeurs Journaliers</div>
        <div style={{ marginLeft: 'auto' }}><Btn label="+ Vendeur" /></div>
      </Row>
      <Col style={{ padding: 8, gap: 6 }}>
        {[
          { nom: 'Mamadou Bah', sorties: 2, dette: 0 },
          { nom: 'Fatoumata Diallo', sorties: 1, dette: 15000 },
        ].map((v, i) => (
          <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
            <Row style={{ justifyContent: 'space-between', marginBottom: 6 }}>
              <div>
                <div style={{ fontWeight: 700, color: C.white, fontSize: 11 }}>{v.nom}</div>
                <div style={{ color: C.muted, fontSize: 9 }}>{v.sorties} sortie(s) aujourd'hui</div>
              </div>
              {v.dette > 0 && <Badge label={`Dette: ${v.dette.toLocaleString()} GNF`} color={C.red} />}
            </Row>
            <Row style={{ gap: 5 }}>
              <Btn label="📤 Nouvelle sortie" color={C.yellow} textColor="#000" style={{ fontSize: 9 }} />
              <Btn label="📥 Retour soir" color={C.green} textColor="#fff" style={{ fontSize: 9 }} />
              <Btn label="🖨️ Bon" color={C.card} textColor={C.gray} style={{ border: `1px solid ${C.border}`, fontSize: 9 }} />
            </Row>
          </div>
        ))}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8 }}>
          <div style={{ color: C.muted, fontSize: 9, marginBottom: 4 }}>BON DE SORTIE (aperçu thermique 80mm)</div>
          <div style={{ background: '#fff', color: '#000', borderRadius: 6, padding: '8px', fontFamily: 'monospace', fontSize: 9 }}>
            <div style={{ textAlign: 'center', fontWeight: 900, marginBottom: 3 }}>MON ENTREPRISE</div>
            <div style={{ textAlign: 'center', color: '#555', marginBottom: 5 }}>BON DE SORTIE VENDEUR</div>
            <div style={{ borderTop: '1px dashed #999', margin: '3px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Valise Cabine Noire ×2</span><span>90 000 GNF</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Sac Sport Bleu ×1</span><span>35 000 GNF</span></div>
            <div style={{ borderTop: '1px dashed #999', margin: '3px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900 }}><span>TOTAL</span><span>125 000 GNF</span></div>
          </div>
        </div>
      </Col>
    </Screen>
  );
}

function MockVentes() {
  return (
    <Screen>
      <Row style={{ background: C.card, padding: '7px 10px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ color: C.yellow, fontWeight: 700, fontSize: 11 }}>🛒 Ventes Magasin</div>
      </Row>
      <Row style={{ padding: 8, gap: 8, alignItems: 'flex-start' }}>
        {/* Catalogue */}
        <Col style={{ flex: 1, gap: 5 }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 5, padding: '4px 8px', color: C.muted, fontSize: 10 }}>🔍 Chercher un article…</div>
          {[
            { nom: 'Valise Cabine / Noir', prix: '45 000 GNF' },
            { nom: 'Sac Sport / Bleu M', prix: '35 000 GNF' },
          ].map((a, i) => (
            <Row key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 8px', gap: 6 }}>
              <Col style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: C.white, fontSize: 10 }}>{a.nom}</div>
                <div style={{ color: C.yellow, fontSize: 10 }}>{a.prix}</div>
              </Col>
              <Btn label="+ Ajouter" style={{ fontSize: 9 }} />
            </Row>
          ))}
        </Col>
        {/* Panier */}
        <Col style={{ width: 140, gap: 5 }}>
          <div style={{ color: C.yellow, fontWeight: 700, fontSize: 10 }}>🧺 Panier</div>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: 7 }}>
            <Row style={{ justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: 9, color: C.text }}>Valise Cabine</span>
              <Row style={{ gap: 3 }}>
                <span style={{ background: C.border, borderRadius: 3, padding: '0 4px', fontSize: 10 }}>−</span>
                <span style={{ fontWeight: 700, fontSize: 10 }}>2</span>
                <span style={{ background: C.border, borderRadius: 3, padding: '0 4px', fontSize: 10 }}>+</span>
              </Row>
            </Row>
            <div style={{ color: C.yellow, fontSize: 9, textAlign: 'right' }}>90 000 GNF</div>
          </div>
          <Divider />
          <Row style={{ justifyContent: 'space-between' }}>
            <span style={{ color: C.muted, fontSize: 9 }}>Total</span>
            <span style={{ color: C.yellow, fontWeight: 700, fontSize: 11 }}>90 000 GNF</span>
          </Row>
          <div style={{ color: C.muted, fontSize: 9, marginBottom: 2 }}>Paiement :</div>
          {['Espèces', 'Orange Money', 'MTN Money'].map((m, i) => (
            <div key={i} style={{ background: i === 0 ? C.ydark + '33' : C.card, border: `1px solid ${i === 0 ? C.ydark : C.border}`, borderRadius: 5, padding: '3px 7px', color: i === 0 ? C.yellow : C.gray, fontSize: 9, marginBottom: 2 }}>{m}</div>
          ))}
          <Btn label="✅ Valider la vente" style={{ width: '100%', textAlign: 'center', marginTop: 4 }} />
        </Col>
      </Row>
    </Screen>
  );
}

function MockCommandes() {
  return (
    <Screen>
      <Row style={{ background: C.card, padding: '7px 10px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ color: C.yellow, fontWeight: 700, fontSize: 11 }}>🌐 Commandes en ligne</div>
        <Row style={{ marginLeft: 'auto', gap: 4 }}>
          {['Toutes', 'En attente', 'En livraison'].map((f, i) => (
            <div key={i} style={{ background: i === 0 ? C.ydark : C.card, border: `1px solid ${C.border}`, borderRadius: 5, padding: '2px 7px', color: i === 0 ? '#000' : C.gray, fontSize: 9 }}>{f}</div>
          ))}
        </Row>
      </Row>
      <Col style={{ padding: 8, gap: 6 }}>
        {[
          { id: 42, client: 'Ibrahima Sow', total: '78 000 GNF', statut: 'En livraison', scolor: C.purple },
          { id: 43, client: 'Mariama Camara', total: '45 000 GNF', statut: 'En attente', scolor: '#f97316' },
          { id: 44, client: 'Oumar Traoré', total: '120 000 GNF', statut: 'Livrée', scolor: C.green },
        ].map((c, i) => (
          <Row key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', gap: 8 }}>
            <Col style={{ flex: 1 }}>
              <Row style={{ gap: 6, marginBottom: 3 }}>
                <span style={{ color: C.muted, fontSize: 9 }}>N°{c.id}</span>
                <Badge label={c.statut} color={c.scolor} />
              </Row>
              <div style={{ fontWeight: 700, color: C.white, fontSize: 10 }}>{c.client}</div>
              <div style={{ color: C.yellow, fontSize: 10, marginTop: 1 }}>{c.total}</div>
            </Col>
            <Col style={{ gap: 4 }}>
              <Btn label="Changer statut ▾" color={c.scolor} textColor="#fff" style={{ fontSize: 9 }} />
              <Btn label="🖨️ Bon livraison" color={C.card} textColor={C.gray} style={{ border: `1px solid ${C.border}`, fontSize: 9 }} />
            </Col>
          </Row>
        ))}
      </Col>
    </Screen>
  );
}

function MockCaisse() {
  return (
    <Screen>
      <Row style={{ background: C.card, padding: '7px 10px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ color: C.yellow, fontWeight: 700, fontSize: 11 }}>💰 Caisse</div>
        <div style={{ marginLeft: 'auto' }}><Btn label="+ Dépense" color={C.red} textColor="#fff" /></div>
      </Row>
      <Col style={{ padding: 8, gap: 6 }}>
        <div style={{ background: `linear-gradient(135deg, ${C.ydark}, #92400e)`, borderRadius: 10, padding: 12, textAlign: 'center' }}>
          <div style={{ color: '#fef3c7', fontSize: 9, marginBottom: 2 }}>SOLDE DE CAISSE</div>
          <div style={{ color: '#fff', fontWeight: 900, fontSize: 22 }}>1 245 000 GNF</div>
          <Row style={{ justifyContent: 'center', gap: 12, marginTop: 6 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#fef3c7', fontSize: 8 }}>Entrées</div>
              <div style={{ color: C.green, fontWeight: 700, fontSize: 11 }}>+1 580 000</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#fef3c7', fontSize: 8 }}>Dépenses</div>
              <div style={{ color: C.red, fontWeight: 700, fontSize: 11 }}>−335 000</div>
            </div>
          </Row>
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8 }}>
          <div style={{ color: C.muted, fontSize: 9, marginBottom: 5 }}>JOURNAL DE CAISSE</div>
          {[
            { label: 'Vente magasin N°87', montant: '+45 000 GNF', color: C.green, date: 'Auj. 14:32' },
            { label: 'Dépense — Transport', montant: '−8 000 GNF', color: C.red, date: 'Auj. 11:00' },
            { label: 'Commande livrée N°42', montant: '+78 000 GNF', color: C.blue, date: 'Hier 17:15' },
          ].map((op, i) => (
            <Row key={i} style={{ justifyContent: 'space-between', padding: '5px 0', borderBottom: i < 2 ? `1px solid ${C.border}` : 'none' }}>
              <Col>
                <span style={{ color: C.text, fontSize: 10 }}>{op.label}</span>
                <span style={{ color: C.muted, fontSize: 9 }}>{op.date}</span>
              </Col>
              <span style={{ color: op.color, fontWeight: 700, fontSize: 10 }}>{op.montant}</span>
            </Row>
          ))}
        </div>
      </Col>
    </Screen>
  );
}

function MockRapports() {
  return (
    <Screen>
      <Row style={{ background: C.card, padding: '7px 10px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ color: C.yellow, fontWeight: 700, fontSize: 11 }}>📈 Rapports & Statistiques</div>
      </Row>
      <Row style={{ padding: '6px 8px', gap: 4, borderBottom: `1px solid ${C.border}` }}>
        {['CA', 'Bénéfice', 'Vendeurs', 'Stock'].map((t, i) => (
          <div key={i} style={{ background: i === 0 ? C.ydark : C.card, border: `1px solid ${C.border}`, borderRadius: 5, padding: '3px 9px', color: i === 0 ? '#000' : C.gray, fontSize: 9, fontWeight: i === 0 ? 700 : 400 }}>{t}</div>
        ))}
      </Row>
      <Col style={{ padding: 8, gap: 6 }}>
        <Row style={{ gap: 6 }}>
          <Field label="Du" value="2025-05-01" style={{ flex: 1, margin: 0 }} />
          <Field label="Au" value="2025-05-05" style={{ flex: 1, margin: 0 }} />
          <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 0 }}><Btn label="Générer" style={{ marginBottom: 0 }} /></div>
        </Row>
        <Row style={{ gap: 5 }}>
          <StatCard label="Total CA" value="2 450 000 GNF" color={C.yellow} />
          <StatCard label="Bénéfice" value="890 000 GNF" color={C.green} />
        </Row>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8 }}>
          <div style={{ color: C.muted, fontSize: 9, marginBottom: 5 }}>CA PAR CANAL</div>
          {[
            { label: 'Magasin', val: '1 200 000 GNF', pct: 49 },
            { label: 'Vendeurs', val: '850 000 GNF', pct: 35 },
            { label: 'Livraisons', val: '400 000 GNF', pct: 16 },
          ].map((r, i) => (
            <div key={i} style={{ marginBottom: 5 }}>
              <Row style={{ justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 9, color: C.text }}>{r.label}</span>
                <span style={{ fontSize: 9, color: C.yellow, fontWeight: 700 }}>{r.val}</span>
              </Row>
              <div style={{ background: C.border, borderRadius: 3, height: 5 }}>
                <div style={{ background: C.yellow, width: `${r.pct}%`, height: '100%', borderRadius: 3 }} />
              </div>
            </div>
          ))}
        </div>
        <div style={{ color: C.muted, fontSize: 9, marginBottom: 3 }}>EXPORTER :</div>
        <Row style={{ gap: 4, flexWrap: 'wrap' }}>
          {[['CSV', C.green], ['Excel', C.blue], ['PDF', C.red], ['Word', C.indigo]].map(([fmt, col]) => (
            <Btn key={fmt} label={`⬇ ${fmt}`} color={col} textColor="#fff" style={{ fontSize: 9 }} />
          ))}
        </Row>
      </Col>
    </Screen>
  );
}

function MockParametres() {
  return (
    <Screen>
      <Row style={{ background: C.card, padding: '7px 10px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ color: C.yellow, fontWeight: 700, fontSize: 11 }}>⚙️ Paramètres</div>
      </Row>
      <Row style={{ padding: '6px 8px', gap: 4, borderBottom: `1px solid ${C.border}` }}>
        {['Général', 'Catalogues', 'Utilisateurs', 'Sécurité'].map((t, i) => (
          <div key={i} style={{ background: i === 0 ? C.ydark : C.card, border: `1px solid ${C.border}`, borderRadius: 5, padding: '3px 8px', color: i === 0 ? '#000' : C.gray, fontSize: 9, fontWeight: i === 0 ? 700 : 400 }}>{t}</div>
        ))}
      </Row>
      <Col style={{ padding: 8, gap: 5 }}>
        <Field label="Nom de l'entreprise" value="Mon entreprise" />
        <Row style={{ gap: 6 }}>
          <Field label="Téléphone" value="+224 620 000 000" style={{ flex: 1, margin: 0 }} />
          <Field label="Devise" value="GNF" style={{ flex: 1, margin: 0 }} />
        </Row>
        <Field label="Message pied de reçu" value="Merci pour votre achat !" />
        <Btn label="💾 Enregistrer les paramètres" style={{ width: '100%', textAlign: 'center', marginTop: 4 }} />
        <Divider />
        <div style={{ color: C.muted, fontSize: 9, marginBottom: 4 }}>CATÉGORIES (onglet Catalogues)</div>
        {[
          { nom: '🧳 Valises', modeles: 4, tailles: 5 },
          { nom: '👜 Sacs', modeles: 3, tailles: 3 },
        ].map((cat, i) => (
          <Row key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 8px', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, color: C.white, fontSize: 10 }}>{cat.nom}</span>
            <Row style={{ gap: 4 }}>
              <Badge label={`${cat.modeles} modèles`} color={C.blue} />
              <Badge label={`${cat.tailles} tailles`} color={C.purple} />
              <Btn label="✏️" color={C.card} textColor={C.yellow} style={{ border: `1px solid ${C.border}`, padding: '1px 5px', fontSize: 9 }} />
            </Row>
          </Row>
        ))}
        <Btn label="+ Nouvelle catégorie" color={C.indigo} textColor="#fff" style={{ fontSize: 10 }} />
      </Col>
    </Screen>
  );
}

// Maquette « Premiers pas » — feuille de route ordonnée du démarrage.
const MockPremiersPas = () => {
  const etapes = [
    { t: 'Se connecter', d: 'Email + mot de passe', c: C.green, ok: true },
    { t: 'Renseigner le commerce', d: 'Paramètres → Entreprise', c: C.green, ok: true },
    { t: 'Catégories & tailles', d: 'Paramètres → Catalogues', c: C.yellow },
    { t: 'Créer les comptes', d: 'Paramètres → Utilisateurs', c: C.yellow },
    { t: 'Ajouter les produits', d: 'Produits → + Nouveau', c: C.blue },
    { t: 'Fournisseur + appro', d: 'remplit le stock', c: C.blue },
    { t: 'Vérifier le stock', d: 'les quantités apparaissent', c: C.purple },
    { t: 'Vendre', d: 'caisse, vendeurs, commandes', c: C.purple },
  ];
  return (
    <Screen>
      <Row style={{ background: C.card, padding: '8px 12px', borderBottom: `1px solid ${C.border}`, gap: 8 }}>
        <Rocket size={14} color={C.yellow} />
        <div style={{ color: C.white, fontWeight: 700, fontSize: 12 }}>Pour bien démarrer — l'ordre à suivre</div>
      </Row>
      <Col style={{ padding: '10px 12px', gap: 0 }}>
        {etapes.map((e, i) => (
          <Row key={i} style={{ gap: 9, alignItems: 'flex-start' }}>
            <Col style={{ alignItems: 'center', width: 22 }}>
              <div style={{ width: 20, height: 20, borderRadius: 20, background: e.c, color: '#000', fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</div>
              {i < etapes.length - 1 && <div style={{ width: 2, height: 14, background: C.border }} />}
            </Col>
            <Col style={{ paddingBottom: 8 }}>
              <div style={{ color: C.white, fontWeight: 600, fontSize: 11 }}>{e.t}</div>
              <div style={{ color: C.muted, fontSize: 9.5 }}>{e.d}</div>
            </Col>
          </Row>
        ))}
      </Col>
    </Screen>
  );
};

// Maquette « Conditionnements » — un lot mixte « chambre à coucher ».
const MockConditionnement = () => (
  <Screen>
    <Row style={{ background: C.card, padding: '8px 12px', borderBottom: `1px solid ${C.border}`, gap: 8 }}>
      <Boxes size={14} color={C.yellow} />
      <div style={{ color: C.white, fontWeight: 700, fontSize: 12 }}>Nouveau conditionnement</div>
    </Row>
    <Col style={{ padding: '10px 12px', gap: 6 }}>
      <Field label="Nom du conditionnement" value="Chambre à coucher complète" />
      <div style={{ color: C.muted, fontSize: 9, marginBottom: -1 }}>Composants (articles contenus)</div>
      {[['Lit 180×200', '× 1'], ['Matelas 180', '× 1'], ['Armoire 3 portes', '× 1'], ['Coiffeuse + miroir', '× 1'], ['Chevet', '× 2']].map((r, i) => (
        <Row key={i} style={{ justifyContent: 'space-between', background: C.card, border: `1px solid ${C.border}`, borderRadius: 5, padding: '4px 8px' }}>
          <span style={{ color: C.text, fontSize: 10 }}>{r[0]}</span>
          <span style={{ color: C.yellow, fontSize: 10, fontWeight: 700 }}>{r[1]}</span>
        </Row>
      ))}
      <div style={{ color: C.muted, fontSize: 9, padding: '0 4px' }}>Stock dérivé : 3 chambres complètes possibles</div>
      <Divider />
      <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Col>
          <span style={{ color: C.muted, fontSize: 9 }}>Prix du lot</span>
          <span style={{ color: C.white, fontWeight: 700, fontSize: 13 }}>8 500 000 GNF</span>
        </Col>
        <Badge label="− 700 000 d'économie client" color={C.green} />
      </Row>
      <Btn label="Créer le conditionnement" color={C.yellow} style={{ textAlign: 'center', marginTop: 2 }} />
    </Col>
  </Screen>
);

// Maquette « Inventaire » — comptage physique vs théorique + écart.
const MockInventaire = () => {
  const lignes = [
    ['Nike Air 41 blanc', '12', '12', '0', C.gray],
    ['Sandale 39 noir', '8', '6', '−2', C.red],
    ['Sac Tana beige', '5', '7', '+2', C.green],
  ];
  return (
    <Screen>
      <Row style={{ background: C.card, padding: '8px 12px', borderBottom: `1px solid ${C.border}`, gap: 8, justifyContent: 'space-between' }}>
        <Row style={{ gap: 8 }}>
          <ClipboardList size={14} color={C.yellow} />
          <div style={{ color: C.white, fontWeight: 700, fontSize: 12 }}>Inventaire — Boutique centre</div>
        </Row>
        <Badge label="Brouillon" color={C.yellow} />
      </Row>
      <Col style={{ padding: '8px 10px' }}>
        <Row style={{ color: C.muted, fontSize: 9, fontWeight: 700, padding: '2px 4px' }}>
          <span style={{ flex: 2 }}>Article</span>
          <span style={{ flex: 1, textAlign: 'center' }}>Théo.</span>
          <span style={{ flex: 1, textAlign: 'center' }}>Compté</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Écart</span>
        </Row>
        {lignes.map((l, i) => (
          <Row key={i} style={{ fontSize: 10, padding: '4px', borderTop: `1px solid ${C.border}` }}>
            <span style={{ flex: 2, color: C.text }}>{l[0]}</span>
            <span style={{ flex: 1, textAlign: 'center', color: C.gray }}>{l[1]}</span>
            <span style={{ flex: 1, textAlign: 'center', color: C.white, fontWeight: 600 }}>{l[2]}</span>
            <span style={{ flex: 1, textAlign: 'right', color: l[4], fontWeight: 700 }}>{l[3]}</span>
          </Row>
        ))}
        <Divider />
        <Row style={{ justifyContent: 'flex-end', gap: 6 }}>
          <Btn label="Soumettre" color={C.indigo} textColor="#fff" />
          <Btn label="Valider" color={C.green} textColor="#fff" />
        </Row>
      </Col>
    </Screen>
  );
};

// Maquette « Catégories & caractéristiques de variation ».
const MockCategorie = () => {
  const Chip = ({ label }) => (
    <span style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 5, padding: '2px 7px', fontSize: 9.5 }}>{label}</span>
  );
  return (
    <Screen>
      <Row style={{ background: C.card, padding: '8px 12px', borderBottom: `1px solid ${C.border}`, gap: 8 }}>
        <Tags size={14} color={C.yellow} />
        <div style={{ color: C.white, fontWeight: 700, fontSize: 12 }}>Catégorie — Téléphone 📱</div>
      </Row>
      <Col style={{ padding: '10px 12px', gap: 9 }}>
        <div style={{ color: C.muted, fontSize: 9 }}>Caractéristiques de variation (max 2)</div>
        <Col style={{ gap: 5 }}>
          <Row style={{ gap: 8, alignItems: 'center' }}>
            <span style={{ color: C.white, fontWeight: 600, fontSize: 10, width: 64 }}>Capacité</span>
            <Row style={{ gap: 4 }}><Chip label="128 Go" /><Chip label="256 Go" /></Row>
          </Row>
          <Row style={{ gap: 8, alignItems: 'center' }}>
            <span style={{ color: C.white, fontWeight: 600, fontSize: 10, width: 64 }}>Couleur</span>
            <Row style={{ gap: 4 }}><Chip label="Noir" /><Chip label="Blanc" /></Row>
          </Row>
        </Col>
        <Divider />
        <div style={{ color: C.muted, fontSize: 9.5 }}>→ Un produit de cette catégorie se décline en variantes :</div>
        <Row style={{ gap: 4, flexWrap: 'wrap' }}>
          {['128 Go · Noir', '128 Go · Blanc', '256 Go · Noir', '256 Go · Blanc'].map((v, i) => (
            <Badge key={i} label={v} color={C.indigo} />
          ))}
        </Row>
      </Col>
    </Screen>
  );
};

// ─── Données des sections avec maquette ────────────────────────────────────
const SECTIONS = [
  {
    id: 'premiers-pas', icon: Rocket, titre: 'Premiers pas',
    mockup: <MockPremiersPas />,
    contenu: [
      { sous: 'Suivez cet ordre (une seule fois)', texte: "Pour bien démarrer, faites ces étapes dans l'ordre. Chacune prépare la suivante : on ne peut pas vendre un article qui n'a pas encore de stock.\n\n1. Se connecter (email + mot de passe).\n2. Renseigner les infos du commerce : Paramètres → Entreprise.\n3. Créer catégories, modèles et tailles : Paramètres → Catalogues.\n4. Créer les comptes employés/vendeurs : Paramètres → Utilisateurs.\n5. Ajouter les produits et leurs variantes (prix inclus).\n6. Enregistrer un fournisseur puis un approvisionnement → c'est ce qui remplit le stock.\n7. Vérifier le stock : les quantités apparaissent.\n8. Commencer à vendre : caisse, vendeurs ou commandes en ligne." },
      { sous: 'Astuce', texte: "Un produit n'apparaît pas à la caisse ? C'est presque toujours qu'il n'a pas encore de stock. Enregistrez un approvisionnement (section Fournisseurs)." },
      { sous: 'Les 3 façons de vendre', texte: "• Au magasin (caisse) : le client est devant vous.\n• Vendeurs journaliers : sortie le matin, retour le soir.\n• Commandes en ligne : à confirmer puis livrer.\n\nDans les trois cas, le stock et la caisse se mettent à jour automatiquement." },
      { sous: 'Guide imprimable', texte: "Un manuel PDF complet (tous les modules, étape par étape, avec schémas) est disponible pour vos employés. Gardez-le près de la caisse." },
    ]
  },
  {
    id: 'connexion', icon: HelpCircle, titre: 'Connexion & Sécurité',
    mockup: <MockConnexion />,
    contenu: [
      { sous: 'Se connecter', texte: "Ouvrez l'application dans votre navigateur. Saisissez votre email et votre mot de passe, puis cliquez sur « Se connecter ». Votre session reste active jusqu'à déconnexion manuelle." },
      { sous: 'Rôles utilisateurs', texte: "• Propriétaire : accès complet à toutes les fonctionnalités.\n• Employé : produits, stock, vendeurs, ventes, commandes.\n• Vendeur : accès limité à ses propres activités.\n\nLe propriétaire crée les comptes dans Paramètres → Utilisateurs." },
      { sous: 'Changer son mot de passe', texte: "Paramètres → Sécurité → saisir l'ancien mot de passe, le nouveau (deux fois) → « Mettre à jour »." },
    ]
  },
  {
    id: 'dashboard', icon: LayoutDashboard, titre: 'Tableau de Bord',
    mockup: <MockDashboard />,
    contenu: [
      { sous: 'Chiffres du jour', texte: "Ventes magasin, ventes vendeurs, livraisons, total encaissé, articles vendus, commandes en attente : tout s'affiche en temps réel au chargement de la page." },
      { sous: 'Alertes stock', texte: "Les articles sous le seuil d'alerte apparaissent en rouge. Un badge rouge s'affiche aussi sur l'icône Stock dans la barre latérale." },
      { sous: 'Activité récente', texte: "Liste chronologique des dernières ventes, commandes et mouvements de stock. Se rafraîchit à chaque visite." },
      { sous: 'Clôture journalière', texte: "En fin de journée, le bouton « Clôturer la journée » archive les chiffres du jour et prépare les compteurs pour demain." },
    ]
  },
  {
    id: 'categories', icon: Tags, titre: 'Catégories & Variations',
    mockup: <MockCategorie />,
    contenu: [
      { sous: 'La catégorie', texte: "Une catégorie regroupe des produits de même nature (Fer à béton, Meubles, Téléphones, Chaussures…). Vous la créez dans Paramètres → Catalogues. Pour chacune, vous définissez : un nom + une icône, des modèles prédéfinis (noms proposés à la création d'un produit), et ses caractéristiques de variation (ci-dessous)." },
      { sous: 'Caractéristiques de variation : l\'idée clé', texte: "C'est ce qui FAIT VARIER un produit dans la catégorie. Vous en déclarez 0, 1 ou 2 par catégorie (pas plus, pour rester simple) :\n• 0 → produit simple : nom + prix + stock, sans déclinaison. Ex : Ciment.\n• 1 → une dimension. Ex : Fer à béton → Diamètre (8, 10, 12mm).\n• 2 → deux dimensions combinées. Ex : Téléphone → Capacité × Couleur." },
      { sous: 'Comment ça fabrique les variantes', texte: "Pour chaque caractéristique, vous donnez un nom (Diamètre, Capacité, Couleur, Pointure…) et des valeurs suggérées. À la création d'un produit, l'application combine les valeurs et crée une variante par combinaison — chacune avec son prix et son stock. Un libellé lisible est généré tout seul (« 10mm », « 128 Go · Noir ») et s'affiche partout : caisse, stock, reçus." },
      { sous: 'Exemples par métier', texte: "• Matériaux : Fer à béton → Diamètre ; Ciment → aucune (produit simple).\n• Meubles : Armoire → Nombre de portes ; Canapé → Places × Couleur.\n• Mode : Chaussures → Pointure × Couleur.\n• Téléphonie : Smartphone → Capacité × Couleur." },
      { sous: 'Bon réflexe', texte: "Réglez les caractéristiques d'une catégorie AVANT d'y créer beaucoup de produits : c'est là que vous décidez comment vos articles se déclinent. La catégorie est désactivable mais pas supprimable tant qu'elle contient des produits actifs (vos données restent intactes)." },
    ]
  },
  {
    id: 'produits', icon: Package, titre: 'Produits & Catalogue',
    mockup: <MockProduits />,
    contenu: [
      { sous: 'Ajouter un produit', texte: "Cliquez sur « + Nouveau produit ». Remplissez le nom, la catégorie, la description. Ajoutez les variantes (modèle, taille, couleur, prix magasin, prix livraison). Enregistrez." },
      { sous: 'Produit vs variante (à bien comprendre)', texte: "• Le PRODUIT est la fiche générale (nom, catégorie, description, photos). Il sert à regrouper : il ne se vend pas et ne se stocke pas tout seul.\n• La VARIANTE est l'article réel qui se vend et se compte. Son identité = modèle + taille + couleur, avec un code unique attribué automatiquement.\n• Les PRIX et le STOCK sont portés par la variante (et par boutique si vous en avez plusieurs). Deux tailles d'un même produit peuvent donc avoir des prix et des stocks différents." },
      { sous: 'Le prix d\'achat moyen (PAM)', texte: "Vous ne le saisissez jamais à la main. À chaque approvisionnement, l'application met à jour une moyenne pondérée du coût d'achat de la variante. C'est ce PAM qui sert à calculer votre bénéfice dans les rapports." },
      { sous: 'Ajouter des photos', texte: "Cliquez sur l'icône 📷 d'un produit. Sélectionnez une ou plusieurs images. Cliquez sur l'étoile pour définir la photo principale." },
      { sous: 'Recherche et filtres', texte: "Barre de recherche par nom + menu déroulant par catégorie. 20 produits par page, navigation par pagination." },
    ]
  },
  {
    id: 'conditionnement', icon: Boxes, titre: 'Conditionnements (Cartons & Lots)',
    mockup: <MockConditionnement />,
    contenu: [
      { sous: 'À quoi ça sert', texte: "Un conditionnement (tonne, lot, ensemble, carton…) est un article vendable composé d'autres articles, en quantités fixes, avec son PROPRE prix — souvent plus avantageux que la somme à l'unité. Vous le créez dans Produits → Conditionnements." },
      { sous: 'Deux cas, un seul outil', texte: "• IDENTIQUE : plusieurs fois le même article. Ex : 1 tonne de fer à béton 10mm = 56 × la même barre.\n• MIXTE : des articles différents réunis. Ex : 1 chambre à coucher = 1 lit + 1 matelas + 1 armoire + 1 coiffeuse + 2 chevets." },
      { sous: 'Exemples concrets', texte: "• Fer à béton : 1 tonne = 56 barres de Fer 10mm (identique).\n• Valises : 1 lot = 1 Cabine + 1 Moyenne + 1 Grande (3 volumes différents).\n• Chambre à coucher : 1 ensemble = lit + matelas + armoire + coiffeuse + chevets.\n• Bureau : 1 ensemble = table + fauteuil + caisson + chaise visiteur.\n\nDans tous ces cas, l'ensemble a un prix unique, plus avantageux que la somme des articles à l'unité." },
      { sous: 'Comment le créer', texte: "1. Donnez-lui un nom (ex : « Chambre à coucher complète », « Tonne fer 10mm »).\n2. Ajoutez ses composants : pour chaque article, la variante + la quantité contenue (ex : Chevet × 2).\n3. Fixez le prix de l'ensemble (magasin et livraison). L'app affiche l'économie réalisée par le client." },
      { sous: 'La mécanique du stock (le point clé)', texte: "Un conditionnement n'a PAS de stock à lui : il est DÉRIVÉ de ses composants.\n• Disponible = combien d'ensembles COMPLETS on peut former avec le stock des composants. Si un composant manque, plus d'ensemble possible.\n  Ex : il vous reste 3 lits mais seulement 2 matelas → vous ne pouvez vendre que 2 chambres complètes.\n• Vendre 1 ensemble décrémente le stock de chaque composant (quantité vendue × quantité du composant).\n• Vous pouvez toujours vendre les composants à l'unité en parallèle : même stock réel." },
      { sous: 'À l\'achat aussi', texte: "Vous pouvez commander et réceptionner par conditionnement (ex : une tonne de fer). À la réception, l'app décompose l'ensemble en composants : chacun entre en stock, et le prix de l'ensemble est réparti sur les composants au prorata de leur valeur — le coût total reste exact au centime près." },
    ]
  },
  {
    id: 'stock', icon: BarChart3, titre: 'Gestion du Stock',
    mockup: <MockStock />,
    contenu: [
      { sous: 'Consulter le stock', texte: "Tableau listant chaque variante avec son stock disponible, réservé et le seuil d'alerte. Rouge = rupture, orange = niveau faible." },
      { sous: 'Ajustement manuel', texte: "Cliquez sur « Ajuster » en face d'une variante. Choisissez le type (entrée, sortie, correction), la quantité et le motif. Chaque ajustement est horodaté et traçable." },
      { sous: 'Historique des mouvements', texte: "Onglet « Mouvements » : liste de tous les changements de stock avec date, type, quantité et origine (approvisionnement, vente, retour vendeur, ajustement)." },
      { sous: 'Seuil d\'alerte', texte: "Définissez un seuil par variante dans la fiche produit. Quand le stock passe sous ce seuil, un badge rouge apparaît dans la sidebar et sur le tableau de bord." },
    ]
  },
  {
    id: 'inventaire', icon: ClipboardList, titre: 'Inventaire physique',
    mockup: <MockInventaire />,
    contenu: [
      { sous: 'À quoi ça sert', texte: "Comparer le stock RÉEL (compté à la main en rayon) au stock THÉORIQUE (ce que l'application croit avoir), puis corriger automatiquement les écarts. C'est ce qui rattrape les pertes, vols, casses et erreurs de saisie." },
      { sous: 'Les deux chiffres comparés', texte: "• Stock théorique : une photo prise à l'ouverture de l'inventaire. Figé : il ne bouge plus même si vous vendez pendant le comptage.\n• Stock physique : le nombre que vous saisissez après avoir compté à la main." },
      { sous: 'Calculé automatiquement', texte: "• Écart = physique − théorique. Négatif = il manque (perte) ; positif = surplus.\n• Valeur de l'écart = écart × prix d'achat moyen : combien ça représente en argent, pas seulement en quantité.\n• L'en-tête additionne l'écart total et la valeur d'écart totale." },
      { sous: 'Le déroulé (avec validation)', texte: "1. Créer un inventaire (brouillon), rattaché à une boutique.\n2. Saisir le comptage variante par variante (motif d'écart possible). Modifiable tant que brouillon.\n3. Soumettre : le gérant l'envoie au propriétaire ; plus aucune ligne modifiable.\n4. Valider (propriétaire) : applique réellement les corrections — un mouvement « ajustement » par écart, stock aligné sur le comptage, tout est tracé.\n5. Rejeter : renvoi avec motif obligatoire, aucun ajustement appliqué." },
      { sous: 'Le point important', texte: "Tant que l'inventaire n'est pas validé, votre stock ne bouge pas : vous comptez tranquillement. Seule la validation corrige le stock, de façon tracée et vérifiable. Le propriétaire peut valider directement son propre brouillon." },
    ]
  },
  {
    id: 'fournisseurs', icon: Truck, titre: 'Fournisseurs & Approvisionnements',
    mockup: <MockFournisseurs />,
    contenu: [
      { sous: 'Créer un fournisseur', texte: "« + Fournisseur » → nom, téléphone, email, ville, spécialité. La spécialité correspond à la catégorie de produits que le fournisseur livre." },
      { sous: 'Enregistrer un approvisionnement', texte: "Ouvrez la fiche d'un fournisseur → « 📦 Nouvel approvisionnement » → sélectionnez les variantes reçues avec la quantité et le prix d'achat unitaire → Valider. Le stock est mis à jour automatiquement." },
      { sous: 'Comparaison prix', texte: "L'onglet « Comparaison prix » affiche pour chaque produit quel fournisseur propose le meilleur tarif, basé sur l'historique des approvisionnements." },
    ]
  },
  {
    id: 'vendeurs', icon: Users, titre: 'Vendeurs Journaliers',
    mockup: <MockVendeurs />,
    contenu: [
      { sous: 'Créer un vendeur', texte: "« + Vendeur » → prénom, nom, téléphone. Le vendeur apparaît immédiatement dans la liste." },
      { sous: 'Sortie du matin', texte: "« 📤 Nouvelle sortie » → sélectionnez les articles confiés et les quantités → Valider. Un bon de sortie est généré avec le montant attendu en fin de journée. Vous pouvez créer plusieurs sorties dans la même journée." },
      { sous: 'Bon de sortie (impression)', texte: "Cliquez sur « 🖨️ Bon » pour générer le bon de sortie au format thermique 80mm. Il contient les articles, quantités, prix unitaires et le total à remettre, avec les zones de signature." },
      { sous: 'Retour du soir', texte: "« 📥 Retour soir » → saisissez le montant remis et les quantités invendues retournées → Valider. Le système calcule l'écart et crée une dette si le vendeur remet moins que prévu." },
      { sous: 'Gestion des dettes', texte: "Onglet « Dettes » dans la fiche vendeur : liste des dettes avec le solde restant. Cliquez sur « Remboursement » pour enregistrer un paiement partiel ou total." },
    ]
  },
  {
    id: 'ventes', icon: ShoppingCart, titre: 'Ventes Magasin (Caisse)',
    mockup: <MockVentes />,
    contenu: [
      { sous: 'Faire une vente', texte: "Recherchez un article → cliquez pour l'ajouter au panier → ajustez la quantité avec + et − → sélectionnez le mode de paiement → « ✅ Valider la vente »." },
      { sous: 'Modes de paiement', texte: "Espèces, Orange Money, MTN Money, Autre. Les ventes sont séparées par mode dans les rapports de caisse." },
      { sous: 'Reçu thermique', texte: "Un aperçu du reçu s'affiche après chaque vente. Cliquez sur « Imprimer » pour l'envoyer à votre imprimante thermique 80mm. Le reçu contient le logo, la date, les articles ×quantités et le total." },
    ]
  },
  {
    id: 'commandes', icon: Globe, titre: 'Commandes en Ligne',
    mockup: <MockCommandes />,
    contenu: [
      { sous: 'Voir et filtrer les commandes', texte: "Filtrez par statut en cliquant sur les onglets : Toutes, En attente, Confirmée, En livraison, Livrée, Annulée." },
      { sous: 'Cycle de vie', texte: "En attente → Confirmer → En livraison → Livrée. À chaque étape, cliquez sur « Changer statut » pour avancer. Vous pouvez annuler à toutes les étapes sauf « Livrée »." },
      { sous: 'Bon de livraison (impression)', texte: "Cliquez sur « 🖨️ Bon livraison » : génère un reçu thermique 80mm avec les infos client, l'adresse, les articles et le total (livraison comprise)." },
    ]
  },
  {
    id: 'caisse', icon: Wallet, titre: 'Caisse & Trésorerie',
    mockup: <MockCaisse />,
    contenu: [
      { sous: 'Solde de caisse', texte: "Le solde en haut reflète la trésorerie disponible : toutes les ventes encaissées moins toutes les dépenses enregistrées depuis le début." },
      { sous: 'Journal de caisse', texte: "Liste chronologique de toutes les opérations : ventes, encaissements commandes, remboursements vendeurs, dépenses. Filtrez par date avec les champs en haut." },
      { sous: 'Enregistrer une dépense', texte: "« + Dépense » → montant, catégorie (loyer, transport, fournitures…), description → Valider. La dépense est déduite du solde immédiatement." },
    ]
  },
  {
    id: 'rapports', icon: FileText, titre: 'Rapports & Statistiques',
    mockup: <MockRapports />,
    contenu: [
      { sous: '4 types de rapports', texte: "• CA : chiffre d'affaires par canal sur la période.\n• Bénéfice : CA moins coûts d'achat.\n• Vendeurs : classement, taux de retour, dettes.\n• Stock : rotations, ruptures, surstocks." },
      { sous: 'Générer un rapport', texte: "Choisissez l'onglet, saisissez les dates de début et fin, cliquez sur « Générer ». Les tableaux et graphiques se mettent à jour instantanément." },
      { sous: 'Exporter', texte: "4 formats disponibles :\n• ⬇ CSV — pour Excel ou Google Sheets\n• ⬇ Excel (.xlsx) — tableau mis en forme\n• ⬇ PDF — document imprimable avec logo\n• ⬇ Word (.doc) — document éditable" },
    ]
  },
  {
    id: 'parametres', icon: Settings, titre: 'Paramètres',
    mockup: <MockParametres />,
    contenu: [
      { sous: 'Informations du commerce', texte: "Onglet Général : nom, téléphone, email, adresse, ville, devise, message de pied de reçu. Ces infos apparaissent sur tous les documents imprimés." },
      { sous: 'Gestion des catégories', texte: "Onglet Catalogues : créez des catégories. Pour chaque catégorie, définissez les modèles prédéfinis (liste déroulante à la création produit) et les tailles disponibles." },
      { sous: 'Gestion des utilisateurs', texte: "Onglet Utilisateurs : créez des comptes Employé ou Vendeur. Réinitialisez les mots de passe si nécessaire. Désactivez un compte sans le supprimer définitivement." },
      { sous: 'Sécurité', texte: "Onglet Sécurité : changez votre propre mot de passe. Choisissez 8 caractères minimum, mélange de lettres et chiffres." },
    ]
  },
];

// ─── Composant principal ───────────────────────────────────────────────────
export default function AideModal({ onClose }) {
  const [section, setSection] = useState(SECTIONS[0].id);
  const active = SECTIONS.find(s => s.id === section);

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/80 p-4">
      <div className="bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-yellow-500 rounded-xl flex items-center justify-center">
              <HelpCircle size={20} className="text-black" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg leading-tight">Guide d'utilisation</h2>
              <p className="text-gray-400 text-xs">Mon entreprise</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-gray-800">
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">

          {/* Nav gauche */}
          <div className="w-48 flex-shrink-0 border-r border-gray-700 overflow-y-auto bg-gray-900/50">
            {SECTIONS.map(s => {
              const Icon = s.icon;
              const isActive = s.id === section;
              return (
                <button
                  key={s.id}
                  onClick={() => setSection(s.id)}
                  className={`w-full flex items-center gap-2.5 px-4 py-3 text-left transition-colors text-sm
                    ${isActive
                      ? 'bg-yellow-600/20 text-yellow-400 font-semibold border-r-2 border-yellow-500'
                      : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                    }`}
                >
                  <Icon size={14} className="flex-shrink-0" />
                  <span className="leading-tight text-xs">{s.titre}</span>
                  {isActive && <ChevronRight size={12} className="ml-auto" />}
                </button>
              );
            })}
          </div>

          {/* Contenu */}
          <div className="flex-1 overflow-y-auto">
            {active && (
              <div className="p-5 space-y-5">

                {/* Titre section */}
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-yellow-600/20 rounded-lg flex items-center justify-center">
                    <active.icon size={16} className="text-yellow-400" />
                  </div>
                  <h3 className="text-white font-bold text-base">{active.titre}</h3>
                </div>

                {/* Maquette visuelle */}
                <div className="rounded-xl overflow-hidden border border-gray-700 shadow-lg">
                  <div className="bg-gray-800 px-3 py-1.5 flex items-center gap-2 border-b border-gray-700">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                    </div>
                    <div className="flex-1 bg-gray-700 rounded text-gray-400 text-xs px-3 py-0.5 text-center">
                      Mon entreprise
                    </div>
                  </div>
                  <div className="p-1">
                    {active.mockup}
                  </div>
                </div>

                {/* Explications */}
                <div className="space-y-3">
                  {active.contenu.map((item, i) => (
                    <div key={i} className="bg-gray-800/60 rounded-xl p-4 border border-gray-700/50">
                      <p className="text-yellow-400 font-semibold text-sm mb-1.5">👉 {item.sous}</p>
                      <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">{item.texte}</p>
                    </div>
                  ))}
                </div>

              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-700 flex-shrink-0 flex items-center justify-between">
          <p className="text-gray-500 text-xs">Mon entreprise © {new Date().getFullYear()} — Guide généré automatiquement</p>
          <button onClick={onClose} className="px-4 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg transition-colors">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
